import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

/**
 * Kaching-style grouped cart line controls.
 *
 * Rendered inside a grouped cart row (see `snippets/cart-products.liquid`).
 * Handles:
 * - per-unit variant switching (one atomic cart/update call moving a unit
 *   between lines, so existing lines keep their position)
 * - the group quantity stepper when the group spans several cart lines
 * - removing every line of the group at once
 *
 * Events are delegated at the document level: the drawer is re-rendered by
 * section morphing after every cart mutation, which replaces buttons/selects,
 * so element-bound listeners would silently die after the first update.
 *
 * Mutations finish by dispatching a CartUpdateEvent carrying the re-rendered
 * sections, which `cart-items-component` (and the cart bubble) listen for.
 */
class CartLineUnits extends HTMLElement {
  #busy = false;

  get #sectionId() {
    return this.closest('cart-items-component')?.dataset.sectionId;
  }

  #allSectionIds() {
    const ids = new Set();
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        ids.add(component.dataset.sectionId);
      }
    }
    return Array.from(ids);
  }

  #sectionsPayload() {
    return {
      sections: this.#allSectionIds().join(','),
      sections_url: window.location.pathname,
    };
  }

  #setBusy(busy) {
    this.#busy = busy;
    this.closest('cart-items-component')?.classList.toggle('cart-items-disabled', busy);
  }

  /**
   * Swaps one unit of a line to another variant via a single cart/update call.
   * @param {HTMLSelectElement} select
   */
  async swapVariant(select) {
    if (this.#busy) return;

    const lineKey = select.dataset.lineKey;
    const lineQuantity = Number(select.dataset.lineQuantity);
    const newVariantId = select.value;

    if (!lineKey || !newVariantId || newVariantId === select.dataset.currentVariant) return;

    /** @type {Record<string, number>} */
    const updates = { [lineKey]: lineQuantity - 1 };

    // If the target variant already has a line in this group, bump that line
    // instead of adding a new one — Shopify keeps line positions that way.
    const sibling = Array.from(this.querySelectorAll('select[data-line-key]')).find(
      (other) =>
        other instanceof HTMLSelectElement &&
        other.dataset.lineKey !== lineKey &&
        other.dataset.currentVariant === newVariantId
    );

    if (sibling instanceof HTMLSelectElement && sibling.dataset.lineKey) {
      updates[sibling.dataset.lineKey] = Number(sibling.dataset.lineQuantity) + 1;
    } else {
      // cart/update adds the variant when it isn't in the cart yet.
      updates[newVariantId] = 1;
    }

    await this.#update(updates);
  }

  /**
   * Handles the group stepper and group remove buttons (delegated clicks).
   * @param {HTMLElement} button
   */
  handleGroupAction(button) {
    if (this.#busy) return;

    const action = button.dataset.groupAction;

    if (action === 'remove') {
      const keys = (button.dataset.groupKeys || '').split(',').filter(Boolean);
      if (!keys.length) return;
      this.#update(Object.fromEntries(keys.map((key) => [key, 0])));
      return;
    }

    const stepper = this.closest('tr')?.querySelector('.cart-items__group-stepper');
    if (!(stepper instanceof HTMLElement)) return;

    const { firstKey, lastKey } = stepper.dataset;
    const firstQuantity = Number(stepper.dataset.firstQuantity);
    const lastQuantity = Number(stepper.dataset.lastQuantity);

    if (action === 'increment' && firstKey) {
      this.#update({ [firstKey]: firstQuantity + 1 });
    } else if (action === 'decrement' && lastKey) {
      this.#update({ [lastKey]: Math.max(0, lastQuantity - 1) });
    }
  }

  /**
   * Runs a cart/update call and morphs the re-rendered sections in.
   * @param {Record<string, number>} updates
   */
  async #update(updates) {
    const productId = this.dataset.productId;
    this.#setBusy(true);
    try {
      const response = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', { body: JSON.stringify({ updates, ...this.#sectionsPayload() }) })
      );
      const result = await response.json();
      if (!response.ok || result.errors) throw new Error(result.errors || response.statusText);
      this.#applySections(result.sections, productId);
    } catch (error) {
      console.error('cart-line-units: cart update failed', error);
      this.#refresh();
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Dispatches the re-rendered sections so cart-items-component morphs them in,
   * then re-opens the unit list the shopper was just editing.
   * @param {Record<string, string> | undefined} sections
   * @param {string | undefined} productId
   */
  #applySections(sections, productId) {
    const sectionId = this.#sectionId;
    if (!sections || !sectionId) return this.#refresh();

    let itemCount;
    const html = sections[sectionId];
    if (html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const count = parsed.querySelector('[ref="cartItemCount"]')?.textContent;
      itemCount = count ? parseInt(count, 10) : undefined;
    }

    document.dispatchEvent(
      new CartUpdateEvent({}, sectionId, {
        itemCount,
        source: 'cart-line-units',
        sections,
      })
    );

    // The morph above is synchronous; keep the edited group's unit list open.
    if (productId) {
      document
        .querySelector(`cart-line-units[data-product-id="${productId}"] details`)
        ?.setAttribute('open', '');
    }
  }

  #refresh() {
    const sectionId = this.#sectionId;
    if (sectionId) sectionRenderer.renderSection(sectionId, { cache: false });
  }
}

if (!customElements.get('cart-line-units')) {
  customElements.define('cart-line-units', CartLineUnits);

  // Delegated listeners survive section morphs replacing the controls.
  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('.cart-items__unit-select[data-line-key]')) return;
    const units = select.closest('cart-line-units');
    if (units instanceof CartLineUnits) units.swapVariant(select);
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-group-action]');
    if (!(button instanceof HTMLElement)) return;
    const units = button.closest('tr')?.querySelector('cart-line-units');
    if (units instanceof CartLineUnits) units.handleGroupAction(button);
  });
}
